import {langEn, LangType} from 'yougile-lang'
import {ArrUtil, HTMLUtil, logger, ObjUtil, Time} from 'yougile-util'
import {getChecklistsAsText} from './checklist'
import {DataObjectId, isDataObjectId} from './data-object'
import {ReportColumn} from './report'
import {
  extractStickerState,
  extractStickerStateWith,
  StickerStateId,
  TimerStickerEntry,
  timerToInterval
} from './sticker'
import {StickerType} from './sticker-constants'
import {StickerData} from './sticker-data'
import {TaskData} from './task-data'
import {TaskDataService} from './task-data-service'

type ReportStickerFieldKey = string & {
  __type: 'ReportSticker'
}
const createReportStickerKey = (
  stickerId: DataObjectId
): ReportStickerFieldKey => ('sticker:' + stickerId) as ReportStickerFieldKey
export const isReportStickerKey = (k: string): k is ReportStickerFieldKey =>
  k?.startsWith('sticker:')
const getStickerIdFromReportStickerKey = (k: ReportStickerFieldKey) =>
  k.split('sticker:')[1] as DataObjectId

export type ReportFieldKey =
  | 'id-task'
  | 'title'
  | 'description'
  | 'checklist'
  | 'createdAt'
  | 'createdBy'
  | 'assignedTo'
  | 'assignedAt'
  | 'responsible'
  | 'completedAt'
  | 'deadline'
  | 'projectField'
  | 'board'
  | 'column'
  | 'lastMove'
  | 'lastMessage'
  | 'messagesCount'
  | 'timetrackingPlan'
  | 'timetrackingWork'
  | 'timer'
  | 'stopwatch'
  | ReportStickerFieldKey

export interface ReportDataFieldLocalized {
  key: ReportFieldKey
  getType?: () => StickerType
  getName: () => string
  getText: (task: TaskData, taskDataService: TaskDataService) => string | number
  order: (t1: TaskData, t2: TaskData, negate: boolean) => 1 | -1 | 0
}

export type ReportDataField = (msg: LangType) => ReportDataFieldLocalized

const format = (timestamp: number | undefined, noTime?: boolean) =>
  timestamp
    ? Time.formatCustom(timestamp, noTime ? 'DD.MM.YYYY' : 'DD.MM.YYYY hh:mm')
    : ''

const timerToHours = (t: TimerStickerEntry, forward: boolean) => {
  const int = timerToInterval(t, forward)
  if (!t || !int) {
    logger.warn('timerToHours: undefined interval')
    return ''
  }
  return +(int.toSeconds() / 3600).toFixed(1)
}

const allowNum = (s: string): string | number => {
  if (s !== '' && ObjUtil.isDefined(+s)) {
    return +s
  } else {
    return s
  }
}

const compare = <T>(
  a: T,
  b: T,
  negate: boolean,
  isEmpty?: (v: T) => boolean
): 1 | -1 | 0 => {
  if (a === b) {
    return 0
  } else if ((!a && (a as any) !== 0) || isEmpty?.(a)) {
    return 1
  } else if ((!b && (b as any) !== 0) || isEmpty?.(b)) {
    return -1
  }
  const result = a > b ? 1 : -1
  return (negate ? -result : result) as 1 | -1
}

const titleField: ReportDataField = msg => ({
  key: 'title',
  getName: () => msg.fieldTitle,
  getText: t => t.title,
  order: (t1, t2, n) =>
    compare(t1.title?.toLowerCase(), t2.title?.toLowerCase(), n)
})

const idTaskField: ReportDataField = msg => ({
  key: 'id-task',
  getName: () => msg.fieldIdTask,
  getText: t => t.idTask || ' ',
  order: (t1, t2, n) => {
    const [t1Pref, t1Value] = t1?.idTask?.split('-') || []
    const [t2Pref, t2Value] = t2?.idTask?.split('-') || []
    const res = compare(t1Pref?.toLowerCase(), t2Pref?.toLowerCase(), n)
    if (res === 0) {
      return compare(Number(t1Value), Number(t2Value), n)
    }

    return res
  }
})

const descriptionField: ReportDataField = msg => ({
  key: 'description',
  getName: () => msg.fieldDescription,
  getText: (t, taskDataService) =>
    HTMLUtil.extractText(taskDataService.getDescription(t.id) ?? ''),
  order: (t1, t2, n) =>
    compare(t1.description?.toLowerCase(), t2.description?.toLowerCase(), n)
})

const checklistField: ReportDataField = msg => ({
  key: 'checklist',
  getName: () => msg.fieldChecklist,
  getText: (t, taskDataService) =>
    getChecklistsAsText(taskDataService.getTask(t.id)?.data?.checklists),
  order: (t1, t2, n) =>
    compare(
      t1.checklists?.allTexts?.toLowerCase(),
      t2.checklists?.allTexts?.toLowerCase(),
      n
    )
})

export const createdAtField: ReportDataField = msg => ({
  key: 'createdAt',
  getName: () => msg.fieldCreatedAt,
  getText: t => format(t.createTime),
  order: (t1, t2, n) => compare(t1.createTime, t2.createTime, n)
})

const createdByField: ReportDataField = msg => ({
  key: 'createdBy',
  getName: () => msg.fieldCreatedBy,
  getText: t => t.createdByName ?? '',
  order: (t1, t2, neg) =>
    compare(t1.createdByName, t2.createdByName, neg, n => n === '_')
})

const responsibleField: ReportDataField = msg => ({
  key: 'responsible',
  getName: () => msg.fieldResponsible,
  getText: t => t.assignedNames[0] ?? '',
  order: (t1, t2, neg) =>
    compare(t1.assignedNames[0], t2.assignedNames[0], neg, n => n === '_')
})

const assignedToField: ReportDataField = msg => ({
  key: 'assignedTo',
  getName: () => msg.fieldAssignedTo,
  getText: t => t.assignedNames.join(', '),
  order: (t1, t2, neg) =>
    compare(
      t1.assignedNames.join(', '),
      t2.assignedNames.join(', '),
      neg,
      n => n === '_'
    )
})

const assignedAtField: ReportDataField = msg => ({
  key: 'assignedAt',
  getName: () => msg.fieldAssignedAt,
  getText: t => format(t.assignTime),
  order: (t1, t2, n) => compare(t1.assignTime, t2.assignTime, n)
})

const completedAtField: ReportDataField = msg => ({
  key: 'completedAt',
  getName: () => msg.fieldCompletedAt,
  getText: t => format(t.completeTime),
  order: (t1, t2, n) => compare(t1.completeTime, t2.completeTime, n)
})

const extractDeadline = (t: TaskData | undefined) =>
  extractStickerState(t?.deadline)
const deadlineField: ReportDataField = msg => ({
  key: 'deadline',
  getName: () => msg.fieldDeadline,
  getText: t => {
    const dl = extractDeadline(t)
    return dl ? format(dl, !t.deadlineWithTime) : ''
  },
  order: (t1, t2, n) => compare(extractDeadline(t1), extractDeadline(t2), n)
})

const projectField: ReportDataField = msg => ({
  key: 'projectField',
  getName: () => msg.fieldProject,
  getText: t => t.visibleProjectTitle,
  order: (t1, t2, n) =>
    compare(t1.visibleProjectTitle, t2.visibleProjectTitle, n)
})

const boardField: ReportDataField = msg => ({
  key: 'board',
  getName: () => msg.fieldBoard,
  getText: t => t.visibleBoardTitle,
  order: (t1, t2, n) => compare(t1.visibleBoardTitle, t2.visibleBoardTitle, n)
})

const columnField: ReportDataField = msg => ({
  key: 'column',
  getName: () => msg.fieldColumn,
  getText: t => t.visibleColumnTitle,
  order: (t1, t2, n) => compare(t1.visibleColumnTitle, t2.visibleColumnTitle, n)
})

const lastMove = (t: TaskData) =>
  t.moveTimes.length > 0 ? t.moveTimes[t.moveTimes.length - 1] : undefined
const lastMoveField: ReportDataField = msg => ({
  key: 'lastMove',
  getName: () => msg.fieldLastMove,
  getText: t => {
    const lm = lastMove(t)
    return lm ? format(lm) : ''
  },
  order: (t1, t2, n) => compare(lastMove(t1), lastMove(t2), n)
})

const lastMessageField: ReportDataField = msg => ({
  key: 'lastMessage',
  getName: () => msg.fieldlastMessage,
  getText: t => format(t.lastMessageTime),
  order: (t1, t2, n) => compare(t1.lastMessageTime, t2.lastMessageTime, n)
})

const messagesCountField: ReportDataField = msg => ({
  key: 'messagesCount',
  getName: () => msg.fieldMessagesCount,
  getText: t => t.messagesCount,
  order: (t1, t2, n) => compare(t1.messagesCount, t2.messagesCount, n)
})
const extractPlan = (t: TaskData) =>
  extractStickerStateWith(t.timetracking, tt => tt.plan)
const timetrackingPlanField: ReportDataField = msg => ({
  key: 'timetrackingPlan',
  getName: () => msg.tsPlan + ', ' + msg.h + '.',
  getText: t => {
    const plan = extractPlan(t)
    if (plan === undefined || isNaN(plan)) {
      return 0
    }
    return plan
  },
  order: (t1, t2, n) => compare(extractPlan(t1), extractPlan(t2), n)
})

const extractWork = (t: TaskData) =>
  extractStickerStateWith(t.timetracking, tt => tt.work)
const timetrackingWorkField: ReportDataField = msg => ({
  key: 'timetrackingWork',
  getName: () => msg.tsWork + ', ' + msg.h + '.',
  getText: t => {
    const work = extractWork(t)
    if (work === undefined || isNaN(work)) {
      return 0
    }
    return work
  },
  order: (t1, t2, n) => compare(extractWork(t1), extractWork(t2), n)
})

const timerField: ReportDataField = msg => ({
  key: 'timer',
  getName: () => msg.timer + ', ' + msg.h + '.',
  getText: t =>
    extractStickerStateWith(t.timer, tt => timerToHours(tt, true)) ?? '',
  order: (t1, t2, n) => {
    const i1 = extractStickerStateWith(t1.timer, tt =>
      timerToInterval(tt, true)
    )
    const i2 = extractStickerStateWith(t2.timer, tt =>
      timerToInterval(tt, true)
    )
    return compare(
      i1 ? i1.toSeconds() : undefined,
      i2 ? i2.toSeconds() : undefined,
      n
    )
  }
})

const stopwatchField: ReportDataField = msg => ({
  key: 'stopwatch',
  getName: () => msg.stopwatch + ', ' + msg.h + '.',
  getText: t => {
    if (t.stopwatch === 'not-pinned') {
      return ''
    }
    return t.stopwatch === 'not-set'
      ? msg.statsNotSet
      : timerToHours(t.stopwatch, false)
  },
  order: (t1, t2, n) => {
    const i1 =
      t1.stopwatch === 'not-set' || t1.stopwatch === 'not-pinned'
        ? undefined
        : timerToInterval(t1.stopwatch, false)
    const i2 =
      t2.stopwatch === 'not-set' || t2.stopwatch === 'not-pinned'
        ? undefined
        : timerToInterval(t2.stopwatch, false)
    return compare(
      i1 ? i1.toSeconds() : undefined,
      i2 ? i2.toSeconds() : undefined,
      n
    )
  }
})

const getStickerField = (sticker: StickerData): ReportDataField | undefined => {
  if (!sticker) {
    return undefined
  }
  const getState = (
    stateId: StickerStateId
  ): {name: string; color?: string} => {
    if (sticker.type === 'string' || sticker.type === 'sprint') {
      return (sticker.states.index[stateId] as any) ?? {name: ''}
    } else {
      return {name: ''}
    }
  }
  return msg => ({
    key: createReportStickerKey(sticker.id),
    getType: () => sticker.type,
    getName: () => `${msg.sticker} “${sticker.title}”`,
    getText: t => {
      const val = t.stickers[sticker.id]
      if (sticker.type === 'text' || sticker.type === 'numeric') {
        return allowNum(val)
      }
      return allowNum(val ? getState(val).name : '')
    },
    order: (t1, t2, n) => {
      const st1 = t1.stickers[sticker.id]
      const st2 = t2.stickers[sticker.id]

      if (sticker.type === 'text') {
        return compare(st1, st2, n)
      }

      if (sticker.type === 'numeric') {
        const maxStValue = n
          ? Number.NEGATIVE_INFINITY
          : Number.POSITIVE_INFINITY

        const stValue1 = st1 === '' ? maxStValue : Number(st1)
        const stValue2 = st2 === '' ? maxStValue : Number(st2)

        return compare(stValue1, stValue2, n)
      }

      if (!sticker.states?.order) {
        return compare(getState(st1), getState(st2), n)
      } else {
        const ix1 = st1 && sticker.states.order.indexOf(st1)
        const ix2 = st2 && sticker.states.order.indexOf(st2)
        return compare(ix1, ix2, n)
      }
    }
  })
}

export const defaultDataFields = [
  idTaskField,
  titleField,
  assignedToField,
  createdAtField,
  createdByField,
  deadlineField,
  projectField,
  boardField,
  columnField
]

export const getFieldsFromReport = (
  report: ReportColumn,
  allStickers: StickerData[],
  includeAll = false
): ReportDataField[] => {
  if (includeAll) {
    return getAllFields(allStickers)
  }
  if (!report?.fields?.length) {
    return defaultDataFields
  }

  const allFields = getAllFields(allStickers)
  const allFieldsIx: Map<string, ReportDataField> = new Map()
  allFields.forEach(f => allFieldsIx.set(f(langEn).key, f))
  const res: ReportDataField[] = []

  for (const key of report.fields ?? []) {
    const f = allFieldsIx.get(key)
    if (f) {
      res.push(f)
    }
  }
  return res.length > 0 ? res : defaultDataFields
}

export const getAllFields = (allStickers: StickerData[]): ReportDataField[] => {
  const addIf = (type: StickerType, ...fields: ReportDataField[]) =>
    (allStickers ?? []).find(s => s.type === type)
      ? fields
      : ([] as ReportDataField[])

  return [
    idTaskField,
    titleField,
    createdAtField,
    createdByField,
    assignedToField,
    assignedAtField,
    responsibleField,
    completedAtField,
    deadlineField,
    projectField,
    boardField,
    columnField,
    lastMoveField,
    lastMessageField,
    messagesCountField,
    descriptionField,
    checklistField,
    ...addIf('timetracking', timetrackingPlanField, timetrackingWorkField),
    ...addIf('timer', timerField),
    ...addIf('stopwatch', stopwatchField),
    ...allStickers.filter(s => !s.isSystem).map(s => getStickerField(s))
  ] as ReportDataField[]
}

export const getStickersFromTasks = (
  tasks: TaskData[],
  allStickers: StickerData[],
  keys?: ReportFieldKey[] | undefined
): StickerData[] => {
  if (!allStickers) {
    return []
  }
  const stickersIx = ArrUtil.createIndex(allStickers, s => s.id)
  const res: StickerData[] = []
  const ids: Set<DataObjectId> = new Set()
  tasks.forEach(t => {
    ObjUtil.forEach(t.stickers, id => {
      if (!isDataObjectId(id)) {
        return
      }
      if (!ids.has(id)) {
        ids.add(id)
        const sticker = stickersIx.get(id)
        if (sticker && !sticker.deleted) {
          res.push(sticker)
        }
      }
    })
  })
  if (keys) {
    keys.forEach(k => {
      if (isReportStickerKey(k)) {
        const id = getStickerIdFromReportStickerKey(k)
        if (!ids.has(id)) {
          ids.add(id)
          const sticker = stickersIx.get(id)
          if (sticker && !sticker.deleted) {
            res.push(sticker)
          }
        }
      }
    })
  }
  return res
}
